import { HttpRequest } from "./HttpRequest";
import * as messages from "./messages";
import { HRCertificationPayRatesWithSupplementals } from "./messages/Agency";
import { EventSource } from "./system/EventSource";
import { HRAppLoginResponse, AgencyMemberAuthInfo, AuthInfo, HRCaregiverLoginResponse } from "./messages/Auth";
import { HRSubSectionId } from "./messages";
import { HRAppDocumentToStore,  HRAppTemporaryDocument, HRDocumentTypes, HRLogoutTimer, HRQuestion, CaregiverDocumentTypeUploadWithDocumentTypeId, HRDownloadableFilePreviewResponse, HRNewCaregiverOfficeAndCertification } from "./messages/HR";
import { CaregiverAvailability, NullableCaregiverAvailability } from "./messages/Caregiver";
import * as Raven from "raven-js";
import { messageValidator } from "./MessageValidation";
import { HRSectionStageId, AgencyId, CaregiverCertification } from "./messages/ObjectIds";

export interface APIConfig {
    readonly apiBaseUrl: string;
}

export function apiConfigEqual(a: APIConfig, b: APIConfig): boolean {
    return a.apiBaseUrl === b.apiBaseUrl;
}

export class UserLocked {
    protected dummy: UserLocked;
}

const userLocked = new UserLocked();

export function isUserLocked<T>(x: T | UserLocked): x is UserLocked {
    return x === userLocked;
}

export class API {
    private static jsonHeader: [string, string] = ["Content-Type", "application/json"];

    public authExpired = new EventSource<void>();
    public appNeedsUpdate = new EventSource<void>();
    /**
     * Loads state from a previous session, saved with 'save'
     */
    public static Load(apiConfig: APIConfig, savedAuthInfo: string): API {
        const authInfo: AuthInfo = JSON.parse(savedAuthInfo);
        // TODO Catch JSON parse error above + validate that authInfo has correct type
        return new API(apiConfig, authInfo);
    }

    public constructor(public readonly apiConfig: APIConfig,
                       public readonly authInfo: AuthInfo) {
    }

    /**
     * Returns a string that contains the serialized auth info. This can
     * then be saved to persistent storage and loaded again later with 'Load'
     */
    public save(): string {
        return JSON.stringify(this.authInfo);
    }

    private authHeader(): [string, string][] {
        // Todo talk to benny and joe about how to use this
        return [["Authorization", "Token " + this.authInfo.authToken],
            ["X-AccessKey", "Token " + this.authInfo.agencyMemberAuthInfo.agencyMemberAccessKey]];
    }

    private async req<T>(forType: string, method: string, endpoint: string, body?: Object): Promise<T> {
        const url = this.apiConfig.apiBaseUrl + endpoint;
        const req = new HttpRequest(
            method, url,
            (body !== undefined ? [API.jsonHeader] : []).concat(this.authHeader()),
            body !== undefined ? JSON.stringify(body) : null);

        const rsp = await req.fetch();

        if (rsp.status >= 200 && rsp.status < 300) {
            // TODO validate response against JSON schema
            const result = validateApiResponseMessage<T>(forType, rsp.responseText);
            return result;
        } else if (rsp.status === 401) {
            this.authExpired.broadcast(undefined);
            throw new (API.AuthExpiredError)();
        }
        if (rsp.status >= 500 && rsp.status < 600) {
            throw new API.ServerError(rsp.status, url);
        } else {
            // TODO Improve this (benny)
            throw new API.ServerError(rsp.status, url);
        }
    }

    private async req_noResponseBody(method: string, endpoint: string, body?: Object): Promise<void> {
        const url = this.apiConfig.apiBaseUrl + endpoint;
        const req = new HttpRequest(
            method, url,
            (body !== undefined ? [API.jsonHeader] : []).concat(this.authHeader()),
            body !== undefined ? JSON.stringify(body) : null);

        const rsp = await req.fetch();

        if (rsp.status >= 200 && rsp.status < 300) {
            // TODO validate that response has no body
            return;
        } else if (rsp.status === 401) {
            this.authExpired.broadcast(undefined);
            throw new (API.AuthExpiredError)();
        } else if (rsp.status >= 500 && rsp.status < 600) {
            throw new API.ServerError(rsp.status, url);
        } else {
            // TODO Improve this (benny)
            throw new API.ServerError(rsp.status, url);
        }
    }

    public geHRStages(selectedCertification: CaregiverCertification): Promise<messages.SectionStagesByCertificationResponse> {
        return this.req<messages.SectionStagesByCertificationResponse>(
            "SectionStagesByCertificationResponse" ,
            "GET",
            `/hr/agencies/${this.authInfo.agencyMemberAuthInfo.agency.id}/agency_members/${this.authInfo.agencyMemberAuthInfo.agencyMember.id}/caregivers/${this.authInfo.caregiver.id}/certifications/${selectedCertification}/hr_application_section_stages`
        );
    }

    public postUpdateCaregiverFinishedStage(stageId: HRSectionStageId): Promise<void> {
        return this.req_noResponseBody(
            "POST",
            `/hr/agencies/${this.authInfo.agencyMemberAuthInfo.agency.id}/agency_members/${this.authInfo.agencyMemberAuthInfo.agencyMember.id}/caregivers/${this.authInfo.caregiver.id}/completed_stage`,
            { HRSectionStageId: stageId}
        );
    }

    public putSubmitCaregiverCertificationPayRates(subsectionId: HRSubSectionId, rates: HRCertificationPayRatesWithSupplementals): Promise<messages.HRAnswerResult> {
        return this.req<messages.HRAnswerResult>(
            "HRAnswerResult",
            "PUT",
            `/hr/agencies/${this.authInfo.agencyMemberAuthInfo.agency.id}/caregivers/${this.authInfo.caregiver.id}/subsections/${subsectionId}/caregiver_accepted_rates`,
            rates
        );
    }

    public getCaregiverForm(selectedCertification: CaregiverCertification): Promise<messages.HRApplicationForm> {
        const isFromLink = localStorage.getItem("fromCode") !== null;
        return this.req<messages.HRApplicationForm>(
            "HRApplicationForm" ,
            "GET",
            `/hr/agencies/${this.authInfo.agencyMemberAuthInfo.agency.id}/caregivers/${this.authInfo.caregiver.id}/certifications/${selectedCertification}/caregiver_application?mobile=${isFromLink}`
        );
    }

    public putCaregiverForm(question: HRQuestion): Promise<messages.HRAnswerResult> {
        return this.req<messages.HRAnswerResult>(
            "HRAnswerResult",
            "PUT",
            `/hr/agencies/${this.authInfo.agencyMemberAuthInfo.agency.id}/caregivers/${this.authInfo.caregiver.id}/caregiver_application_answer`,
            question
        );
    }

    public uploadCaregiverDocument(caregiverDocument: CaregiverDocumentTypeUploadWithDocumentTypeId): Promise<messages.HRAnswerResult> {
        return this.req<messages.HRAnswerResult>(
            "HRAnswerResult",
            "POST",
            `/hr/agencies/${this.authInfo.agencyMemberAuthInfo.agency.id}/caregivers/${this.authInfo.caregiver.id}/caregiver_document_upload/${caregiverDocument.caregiverDocumentTypeId}`,
            caregiverDocument.caregiverDocumentTypeUpload
        );
    }

    public getCaregiverGeneratedDocuments(): Promise<messages.HRAppDocuments> {
        return this.req<messages.HRAppDocuments>(
            "HRAppDocuments",
            "POST",
            `/hr/agencies/${this.authInfo.agencyMemberAuthInfo.agency.id}/caregivers/${this.authInfo.caregiver.id}/generate_documents`);
    }

    public async caregiverUpdateProfile(params: messages.CaregiverUpdateParams): Promise<boolean> {
        try {
        await this.req_noResponseBody("PATCH", `/agencies/${this.authInfo.agencyMemberAuthInfo.agency.id}/caregiver/${this.authInfo.caregiver.id}`, params);
        return true;
        } catch (err) {
            if (err instanceof API.ServerError) {
                if (err.statusCode === 409) {
                    return false;
                }
            }
            throw err;
        }
    }

    public caregiverUploadPhoto(dataUri: string): Promise<void> {
        const body: messages.PhotoUpload = {
            photoData: dataUri
        };
        return this.req_noResponseBody("PUT", `/caregivers/${this.authInfo.caregiver.id}/photo`, body);
    }

    public setCaregiverStatus(selectedCertification: CaregiverCertification): Promise<void> {
        const hrNewCaregiverOffice: HRNewCaregiverOfficeAndCertification = {
            officeId: this.authInfo.caregiver.officeIds[0],
            certification: selectedCertification
        };
        return this.req_noResponseBody("PUT", `/hr/agencies/${this.authInfo.agencyMemberAuthInfo.agency.id}/caregivers/${this.authInfo.caregiver.id}/add_to_agency`, hrNewCaregiverOffice);
    }

    public setCaregiverAvailability(availability: CaregiverAvailability): Promise<void> {
        return this.req_noResponseBody("PUT", `/caregivers/${this.authInfo.caregiver.id}/availability`, availability);
    }

    public getCaregiverAvailability(): Promise<NullableCaregiverAvailability> {
        return this.req<NullableCaregiverAvailability>("NullableCaregiverAvailability", "GET", `/caregivers/${this.authInfo.caregiver.id}/availability`);
    }

    public getCaregiverGeneratedDocument(
        subSectionId: HRSubSectionId,
        selectedCertification: CaregiverCertification): Promise<HRAppTemporaryDocument> {
        return this.req<HRAppTemporaryDocument>("HRAppTemporaryDocument", "POST", `/hr/agencies/${this.authInfo.agencyMemberAuthInfo.agency.id}/caregivers/${this.authInfo.caregiver.id}/certification/${selectedCertification}/subsection/${subSectionId}/generate_temp`);
    }

    public confirmCaregiverDocument(subSectionId: HRSubSectionId, body: HRAppDocumentToStore): Promise<void> {
        return this.req_noResponseBody("POST", `/hr/agencies/${this.authInfo.agencyMemberAuthInfo.agency.id}/caregivers/${this.authInfo.caregiver.id}/subsection/${subSectionId}/confirm`, body);
    }

    public getDocumentTypesForCertification(selectedCertification: CaregiverCertification): Promise<HRDocumentTypes> {
        return this.req<HRDocumentTypes>("HRDocumentTypes", "GET", `/hr/agencies/${this.authInfo.agencyMemberAuthInfo.agency.id}/caregivers/${this.authInfo.caregiver.id}/certification/${selectedCertification}/mandatory_document_types`);
    }

    public getAgencyLogoutTimerInMilliSeconds(): Promise<HRLogoutTimer> {
        return this.req<HRLogoutTimer>("HRLogoutTimer", "GET", `/hr/agencies/${this.authInfo.agencyMemberAuthInfo.agency.id}/logout_timer`);

    }
    public getDownloadableFile(question: HRQuestion): Promise<HRDownloadableFilePreviewResponse> {
        return this.req<HRDownloadableFilePreviewResponse>("HRDownloadableFilePreviewResponse", "PUT",
            `/hr/agencies/${this.authInfo.agencyMemberAuthInfo.agency.id}/caregivers/` +
            `${this.authInfo.caregiver.id}/downloadable_files/file_url`, question);
    }
}

export namespace API {
    /**
     * Signals that the server returned an error
     */
    export class ServerError extends Error {
        __proto__: Error; // tslint:disable-line:variable-name
        constructor(statusCode: number, url: string) {
            const trueProto = new.target.prototype;
            super(`Server returned an error: ${statusCode}, ${url}`);

            this.__proto__ = trueProto;

            this.statusCode = statusCode;
            this.url = url;
        }

        readonly statusCode: number;
        readonly url: string;
    }

    /**
     * Signals that the server returned an error
     */
    export class StatusCodeError extends Error {
        __proto__: Error; // tslint:disable-line:variable-name
        constructor(statusCode: number, url: string, body: string) {
            const trueProto = new.target.prototype;
            super(`Server returned an error: ${statusCode}, ${url}`);

            this.__proto__ = trueProto;

            this.statusCode = statusCode;
            this.url = url;
            this.body = body;
        }

        readonly statusCode: number;
        readonly url: string;
        readonly body: string;
    }

    /**
     * Signals that the server returned invalid JSON or data not valid with the expected schema
     */
    export class InvalidResponseError extends Error {
        __proto__: Error; // tslint:disable-line:variable-name
        constructor(message: string, detail: string, responseJSON: any, errorJSON: any) {
            const trueProto = new.target.prototype;
            super(`Unexpected Server Response: ${message}\n${detail}\n${JSON.stringify(errorJSON)}\n${JSON.stringify(responseJSON)}`);

            this.__proto__ = trueProto;

            this.detail = detail;
            this.responseJSON = responseJSON;
            this.errorJSON = errorJSON;
        }

        readonly detail: string;
        readonly responseJSON: any;
        readonly errorJSON: any;
    }


    /**
     * Signals that our AuthToken has expired, and we must login again
     */
    export class AuthExpiredError extends Error {
        __proto__: Error; // tslint:disable-line:variable-name
        constructor() {
            const trueProto = new.target.prototype;
            super("AuthToken expired or invalid");

            this.__proto__ = trueProto;
        }
    }
    /**
     * Auth user blocked error
     */
    export class AuthUserBlockedError extends Error {
        __proto__: Error; // tslint:disable-line:variable-name
        constructor() {
            const trueProto = new.target.prototype;
            super("User account is blocked");

            this.__proto__ = trueProto;
        }
    }

    /**
     * Signals that the App version is too old and the user should be instructed to update
     */
    export class AppNeedsUpdateError extends Error {
        __proto__: Error; // tslint:disable-line:variable-name
        constructor() {
            const trueProto = new.target.prototype;
            super("You must update the MedFlyt app to the latest version");

            this.__proto__ = trueProto;
        }
    }

}

export async function linkAuthGetData(apiConfig: APIConfig, shortCode: string): Promise<AgencyMemberAuthInfo> {
    const url = apiConfig.apiBaseUrl + `/code_auth/` + shortCode;
    const req = new HttpRequest(
        "GET", url,
        [["Content-Type", "application/json"]], null);

    const rsp = await req.fetch();

    if (rsp.status >= 200 && rsp.status < 300) {
        const result = validateApiResponseMessage<AgencyMemberAuthInfo>("AgencyMemberAuthInfo", rsp.responseText);
        return result;
    } else if (rsp.status === 401) {
        throw new (API.AuthExpiredError)();
    }
    if (rsp.status >= 500 && rsp.status < 600) {
        throw new API.ServerError(rsp.status, url);
    } else {
        throw new API.ServerError(rsp.status, url);
    }
}

/**
 * Returns null if user doesn't exist.
 *
 * Otherwise returns false
 */
export async function hrLogin(apiConfig: APIConfig, loginRequest: messages.LoginRequest): Promise<HRAppLoginResponse | null | UserLocked> {
    const url = apiConfig.apiBaseUrl + `/hr/auth/login`;
    const req = new HttpRequest(
        "POST", url,
        [["Content-Type", "application/json"]],
        JSON.stringify(loginRequest));
    const rsp = await req.fetch();
    if (rsp.status === 401) {
        return null;
    } else if (rsp.status === 423) {
        return userLocked;
    } else if (rsp.status >= 200 && rsp.status < 300) {
        const response: messages.HRAppLoginResponse = JSON.parse(rsp.responseText);
        return response;
    } else {
        throw new API.ServerError(rsp.status, url);
    }
}


/**
 * Returns true if the phoneNumber was valid and exists in the server.
 *
 * Otherwise returns false
 */
export async function requestCode(apiConfig: APIConfig, phoneNumber: string): Promise<boolean> {
    const url = apiConfig.apiBaseUrl + `/auth/caregiver/${phoneNumber}/request_code`;
    const req = new HttpRequest(
        "POST", url,
        [],
        null);
    const rsp = await req.fetch();

    if (rsp.status === 404) {
        return false;
    } else if (rsp.status >= 200 && rsp.status < 300) {
        // TODO validate that there is no response body
        return true;
    } else {
        throw new API.ServerError(rsp.status, url);
    }
}

/**
 * @return null if the login code was incorrect
 */
export async function login(apiConfig: APIConfig, agencyId: AgencyId, phoneNumber: string, params: messages.CaregiverLoginRequest): Promise<HRCaregiverLoginResponse | null | UserLocked> {
    const req = new HttpRequest(
        "POST", apiConfig.apiBaseUrl + `/auth/agencies/${agencyId}/caregiver/${phoneNumber}/hr_login`,
        [
            ["Content-Type", "application/json"]
        ],
        JSON.stringify(params));

    const rsp = await req.fetch();

    if (rsp.status >= 200 && rsp.status < 300) {
        // TODO validate response against JSON schema
        const result = validateApiResponseMessage<messages.HRCaregiverLoginResponse>("HRCaregiverLoginResponse", rsp.responseText);
        return result;
    } else if (rsp.status === 401) {
        return null;
    } else if (rsp.status === 423) {
        return userLocked;
    } else {
        throw new Error("Internal Server Error: " + rsp.status);
    }
}

export async function newCaregiver(apiConfig: APIConfig, authToken: string, params: messages.CaregiverCreateParams): Promise<messages.CaregiverLoginResponse | null> {
    const req = new HttpRequest(
        "POST", apiConfig.apiBaseUrl + `/caregiver/new/`,
        [
            ["Content-Type", "application/json"],
            ["Authorization", "Token " + authToken]
        ],
        JSON.stringify(params));

    const rsp = await req.fetch();

    if (rsp.status >= 200 && rsp.status < 300) {
        // TODO validate response against JSON schema
        const result = validateApiResponseMessage<messages.CaregiverLoginResponse>("CaregiverLoginResponse", rsp.responseText);
        return result;
    } else if (rsp.status === 409) {
        return null;
    } else {
        throw new Error("Internal Server Error: " + rsp.status);
    }
}

/**
 * @param forType
 * @param val
 */
export function validateApiResponseMessage<T>(forType: string, val: string): T {
    let data: any;
    try {
        data = JSON.parse(val);
    } catch (e) {
        Raven.captureException(e);
        throw new API.InvalidResponseError("Invalid JSON", e.message, undefined, undefined);
    }

    const validate = messageValidator(forType);
    if (validate(data) !== true) {
        Raven.captureException(new Error(`Error validating against JSON schema: ${JSON.stringify(validate.errors)}\n${val}`));
        throw new API.InvalidResponseError("JSON schema validation error", "", JSON.parse(val), validate.errors);
    }

    return data;
}



// WEBPACK FOOTER //
// ./src/API.ts